import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import {
  fetchBillingReportEmployees,
  submitBillingReport,
} from '../../../Api/request/billingReport';

export const BillingReportEmployeeDropdown = ({
  label,
  placeholder,
  value,
  options,
  optionField,
  onSelect,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const fieldRef = useRef(null);
  const filteredOptions = options.filter((employee) => (
    `${employee[optionField] || ''}`.toLowerCase().includes(searchValue.trim().toLowerCase())
  ));

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleOutsideClick = (event) => {
      if (fieldRef.current && !fieldRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setSearchValue('');
  }, [isOpen]);

  return (
    <label ref={fieldRef} className="billing-report-dialog__field">
      <span>{label}*</span>
      <span className="billing-report-employee-dropdown">
        <button
          type="button"
          className="billing-report-employee-dropdown__control"
          aria-expanded={isOpen}
          disabled={disabled}
          onClick={() => setIsOpen((current) => !current)}
        >
          <span className={value ? '' : 'is-placeholder'}>{value || placeholder}</span>
          <i className="pi pi-chevron-down" aria-hidden="true" />
        </button>
        {isOpen && (
          <span className="billing-report-employee-dropdown__menu">
            <span className="billing-report-employee-dropdown__search">
              <i className="pi pi-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search..."
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                onClick={(event) => event.stopPropagation()}
              />
            </span>
            <span className="billing-report-employee-dropdown__options">
              {filteredOptions.length ? filteredOptions.map((employee) => (
                <button
                  key={employee.employeeId}
                  type="button"
                  onClick={() => {
                    onSelect(employee);
                    setIsOpen(false);
                  }}
                >
                  {employee[optionField]}
                </button>
              )) : (
                <span className="billing-report-employee-dropdown__empty">No options found</span>
              )}
            </span>
          </span>
        )}
      </span>
    </label>
  );
};

BillingReportEmployeeDropdown.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    employeeId: PropTypes.string,
    employeeName: PropTypes.string,
  })).isRequired,
  optionField: PropTypes.oneOf(['employeeId', 'employeeName']).isRequired,
  onSelect: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

BillingReportEmployeeDropdown.defaultProps = {
  value: '',
  disabled: false,
};

const BillingReportAddDialog = ({ rows, canAddAmount, onClose, onSave }) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [billedEffort, setBilledEffort] = useState('0');
  const [totalAmountInr, setTotalAmountInr] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const existingEmployeeIds = useMemo(() => new Set(rows
    .map((row) => `${row.employeeId || ''}`.trim())
    .filter(Boolean)), [rows]);
  const availableEmployees = useMemo(() => employees.filter((employee) => (
    !existingEmployeeIds.has(`${employee.employeeId}`)
  )), [employees, existingEmployeeIds]);
  const selectedEmployee = employees.find((employee) => (
    employee.employeeId === selectedEmployeeId
  ));

  useEffect(() => {
    let isMounted = true;
    fetchBillingReportEmployees()
      .then((response) => {
        if (isMounted) setEmployees(response?.data?.data || []);
      })
      .catch((error) => {
        if (isMounted) {
          setLoadError(error?.response?.data?.message || 'Unable to load employees');
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const selectEmployee = (employee) => {
    setSelectedEmployeeId(employee.employeeId);
    setBilledEffort('0');
    setTotalAmountInr('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedEmployee || existingEmployeeIds.has(selectedEmployee.employeeId)) return;
    await onSave({
      ...selectedEmployee,
      loggedEffort: 0,
      billedEffort: Number(billedEffort),
      ...(canAddAmount ? { totalAmountInr: Number(totalAmountInr) } : {}),
    });
  };

  return (
    <div className="billing-report-dialog-overlay" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <form
        className="billing-report-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-report-dialog-title"
        onSubmit={handleSubmit}
      >
        <header className="billing-report-dialog__header">
          <h2 id="billing-report-dialog-title">Add Billing Effort</h2>
          <button type="button" aria-label="Close" onClick={onClose}>×</button>
        </header>
        <div className="billing-report-dialog__body">
          <BillingReportEmployeeDropdown
            label="Employee Name"
            placeholder="Select Employee Name"
            value={selectedEmployee?.employeeName || ''}
            options={availableEmployees}
            optionField="employeeName"
            onSelect={selectEmployee}
            disabled={isLoading || Boolean(loadError) || !availableEmployees.length}
          />
          <BillingReportEmployeeDropdown
            label="Employee ID"
            placeholder="Select Employee ID"
            value={selectedEmployee?.employeeId || ''}
            options={availableEmployees}
            optionField="employeeId"
            onSelect={selectEmployee}
            disabled={isLoading || Boolean(loadError) || !availableEmployees.length}
          />
          <label className="billing-report-dialog__field">
            <span>Logged Effort</span>
            <input value={selectedEmployee ? '0' : ''} readOnly />
          </label>
          <label className="billing-report-dialog__field">
            <span>Billed Effort*</span>
            <input
              type="number"
              min="0"
              step="any"
              value={billedEffort}
              onChange={(event) => setBilledEffort(event.target.value)}
              required
            />
          </label>
          {canAddAmount && (
            <label className="billing-report-dialog__field">
              <span>Total Amount (INR)*</span>
              <input
                type="number"
                min="0"
                step="any"
                value={totalAmountInr}
                onChange={(event) => setTotalAmountInr(event.target.value)}
                required
              />
            </label>
          )}
          {isLoading && <span className="billing-report-dialog__message">Loading employees...</span>}
          {loadError && <span className="billing-report-dialog__error">{loadError}</span>}
          {!isLoading && !loadError && !availableEmployees.length && (
            <span className="billing-report-dialog__message">
              All active employees are already included in this report.
            </span>
          )}
        </div>
        <footer className="billing-report-dialog__footer">
          <button type="button" className="billing-report-dialog__cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="billing-report-dialog__save"
            disabled={!selectedEmployee || isLoading || Boolean(loadError)}
          >
            Save
          </button>
        </footer>
      </form>
    </div>
  );
};

BillingReportAddDialog.propTypes = {
  rows: PropTypes.arrayOf(PropTypes.shape({
    employeeId: PropTypes.string,
  })).isRequired,
  canAddAmount: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

BillingReportAddDialog.defaultProps = {
  canAddAmount: false,
};

export { BillingReportAddDialog };

export const BillingReportApproverDialog = ({
  open,
  selectedApprover,
  projectId,
  periodStartDate,
  periodEndDate,
  userInfo,
  onClose,
  onSave,
}) => {
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedClientApprover, setSelectedClientApprover] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    let isMounted = true;
    setIsLoading(true);
    setLoadError('');
    fetchBillingReportEmployees()
      .then((response) => {
        if (!isMounted) return;
        const employeeOptions = response?.data?.data || [];
        setEmployees(employeeOptions);
        setSelectedEmployee(employeeOptions.find((employee) => (
          `${employee.employeeId}` === `${selectedApprover?.employeeId || ''}`
        )) || null);
        setSelectedClientApprover(null);
      })
      .catch((error) => {
        if (isMounted) {
          setLoadError(error?.response?.data?.message || 'Unable to load employees.');
        }
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, [open, selectedApprover?.employeeId]);

  const handleSave = async () => {
    if (!selectedEmployee?.employeeId || isSaving) return;
    setIsSaving(true);
    try {
      const response = await submitBillingReport({
        projectId,
        periodStartDate,
        periodEndDate,
        approverId: selectedEmployee.employeeId,
        approver2Id: selectedClientApprover?.employeeId,
        userInfo,
      });
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Unable to submit the billing report.');
      }
      toast.success(response.data.message || 'Billing report submitted for approval.');
      await onSave(selectedEmployee);
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Unable to submit the billing report.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="billing-report-dialog-overlay" role="presentation" onMouseDown={(event) => {
      if (!isSaving && event.target === event.currentTarget) onClose();
    }}>
      <div
        className="billing-report-approver-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-report-approver-title"
      >
        <header className="billing-report-approver-dialog__header">
          <h2 id="billing-report-approver-title">Select Approvers</h2>
        </header>
        <div className="billing-report-approver-dialog__body">
          <BillingReportEmployeeDropdown
            label="Approver"
            placeholder="Select Approver"
            value={selectedEmployee?.employeeName || ''}
            options={employees}
            optionField="employeeName"
            onSelect={(employee) => {
              setSelectedEmployee(employee);
              if (`${selectedClientApprover?.employeeId || ''}` === `${employee.employeeId}`) {
                setSelectedClientApprover(null);
              }
            }}
            disabled={isLoading || Boolean(loadError)}
          />
          <BillingReportEmployeeDropdown
            label="Client Approver"
            placeholder="Select Client Approver"
            value={selectedClientApprover?.employeeName || ''}
            options={employees.filter((employee) => (
              `${employee.employeeId}` !== `${selectedEmployee?.employeeId || ''}`
            ))}
            optionField="employeeName"
            onSelect={setSelectedClientApprover}
            disabled={isLoading || Boolean(loadError)}
          />
          {isLoading && <span className="billing-report-dialog__message">Loading employees...</span>}
          {loadError && <span className="billing-report-dialog__error">{loadError}</span>}
        </div>
        <footer className="billing-report-approver-dialog__footer">
          <button type="button" disabled={isSaving} onClick={onClose}>Cancel</button>
          <button
            type="button"
            disabled={!selectedEmployee || !selectedClientApprover || isLoading || Boolean(loadError) || isSaving}
            onClick={handleSave}
          >
            {isSaving ? 'Submitting...' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  );
};

BillingReportApproverDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  selectedApprover: PropTypes.shape({
    employeeId: PropTypes.string,
    employeeName: PropTypes.string,
  }),
  projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  periodStartDate: PropTypes.string.isRequired,
  periodEndDate: PropTypes.string.isRequired,
  userInfo: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

BillingReportApproverDialog.defaultProps = {
  selectedApprover: null,
};

export const BILLING_REPORT_AMOUNT_METHODS = [
  'Per day rate / Man day rate',
  'Per Hour Rate',
  'Man Months',
  'Monthly prorate basis',
  'Fixed monthly Cost per project',
  'Monthly prorate basis and Per day rate',
];

export const BillingReportAmountMethodDialog = ({
  open,
  selectedMethod,
  onClose,
  onSave,
}) => {
  const [draftMethod, setDraftMethod] = useState(selectedMethod || '');

  useEffect(() => {
    if (open) setDraftMethod(selectedMethod || '');
  }, [open, selectedMethod]);

  if (!open) return null;
  return (
    <div
      className="billing-report-dialog-overlay"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="billing-report-amount-method-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="billing-report-amount-method-title"
      >
        <header className="billing-report-amount-method-dialog__header">
          <h2 id="billing-report-amount-method-title">Amount type</h2>
        </header>
        <div className="billing-report-amount-method-dialog__body">
          <label htmlFor="billing-report-amount-method">Select amount type*</label>
          <select
            id="billing-report-amount-method"
            value={draftMethod}
            onChange={(event) => setDraftMethod(event.target.value)}
          >
            <option value="" disabled hidden>Select amount type</option>
            {BILLING_REPORT_AMOUNT_METHODS.map((method) => (
              <option key={method} value={method}>{method}</option>
            ))}
          </select>
        </div>
        <footer className="billing-report-amount-method-dialog__footer">
          <button type="button" onClick={onClose}>Cancel</button>
          <button
            type="button"
            disabled={!draftMethod}
            onClick={() => {
              onSave(draftMethod);
              onClose();
            }}
          >
            Apply
          </button>
        </footer>
      </div>
    </div>
  );
};

BillingReportAmountMethodDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  selectedMethod: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

BillingReportAmountMethodDialog.defaultProps = {
  selectedMethod: '',
};

